import { Head, router } from "@inertiajs/react"
import { CheckCircledIcon, CrossCircledIcon, StopwatchIcon } from "@radix-ui/react-icons"
import { useEffect, useMemo, useState } from "react"

import { AppShell } from "@/components/app-shell"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const statusStyles = {
  queued: "bg-[rgb(233_196_106_/_0.18)] text-[rgb(130_92_15)]",
  processing: "bg-[rgb(42_157_143_/_0.14)] text-[var(--color-charcoal-blue)]",
  indexed: "bg-[rgb(61_150_111_/_0.14)] text-[rgb(25_111_76)]",
  failed: "bg-[rgb(197_75_75_/_0.12)] text-[var(--color-danger)]"
}

function statusIcon(status) {
  if (status === "indexed") return <CheckCircledIcon className="h-4 w-4" />
  if (status === "failed") return <CrossCircledIcon className="h-4 w-4" />

  return <StopwatchIcon className="h-4 w-4" />
}

function formatStatus(value) {
  return value.replace("_", " ")
}

export default function OrganizationOnboardingPage({
  redirect_url,
  status: initialStatus,
  status_endpoint
}) {
  const [status, setStatus] = useState(initialStatus)
  const [redirecting, setRedirecting] = useState(false)
  const complete = Boolean(status?.complete)
  const total = status?.total || 0
  const settled = (status?.indexed || 0) + (status?.failed || 0)
  const progress = total === 0 ? 0 : Math.round((settled / total) * 100)
  const subtitle = complete
    ? "Initial seeding is complete. Opening the portal now."
    : "Indexing is running. This page will move forward when the organization queue is clear."

  const summaryItems = useMemo(() => [
    { label: "Queued", value: status?.queued || 0 },
    { label: "Processing", value: status?.processing || 0 },
    { label: "Indexed", value: status?.indexed || 0 },
    { label: "Failed", value: status?.failed || 0 },
    { label: "Queue jobs", value: status?.sidekiq_indexing_jobs || 0 }
  ], [status])

  useEffect(() => {
    if (!status_endpoint || complete) return undefined

    const poll = window.setInterval(async () => {
      const response = await fetch(status_endpoint, {
        headers: {
          Accept: "application/json"
        }
      })

      if (!response.ok) return

      setStatus(await response.json())
    }, 2500)

    return () => window.clearInterval(poll)
  }, [complete, status_endpoint])

  useEffect(() => {
    if (!complete || redirecting) return undefined

    setRedirecting(true)
    const timeout = window.setTimeout(() => {
      router.visit(status?.redirect_url || redirect_url)
    }, 900)

    return () => window.clearTimeout(timeout)
  }, [complete, redirect_url, redirecting, status?.redirect_url])

  return (
    <>
      <Head title="Initial seeding" />

      <AppShell title="Initial seeding" subtitle={subtitle}>
        <div className="grid gap-6">
          <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-medium text-[var(--color-charcoal-blue)]">
                  {settled} of {total} documents settled
                </p>
                <div className="mt-3 h-3 w-full min-w-[16rem] overflow-hidden rounded-full bg-[var(--color-panel)] md:w-[24rem]">
                  <div
                    className="h-full rounded-full bg-[var(--color-verdigris)] transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <Button
                disabled={!complete}
                onClick={() => router.visit(status?.redirect_url || redirect_url)}
                type="button"
              >
                Open portal
              </Button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {summaryItems.map((item) => (
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-3" key={item.label}>
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-faint)]">{item.label}</p>
                  <p className="mt-1 text-2xl font-semibold text-[var(--color-charcoal-blue)]">{item.value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">Documents</h2>

            <div className="mt-4 divide-y divide-[var(--color-border)]">
              {(status?.documents || []).map((document) => (
                <article className="grid gap-3 py-4 md:grid-cols-[1fr_auto]" key={document.id}>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-[var(--color-charcoal-blue)]">{document.title}</p>
                    <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                      {document.pages_count} pages, {document.chunks_count} chunks
                    </p>
                    {document.failure_reason ? (
                      <p className="mt-2 text-sm text-[var(--color-danger)]">{document.failure_reason}</p>
                    ) : null}
                  </div>

                  <span className={cn(
                    "inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-medium capitalize",
                    statusStyles[document.status] || statusStyles.queued
                  )}>
                    {statusIcon(document.status)}
                    {formatStatus(document.status)}
                  </span>
                </article>
              ))}
            </div>
          </section>
        </div>
      </AppShell>
    </>
  )
}
